import {
  User,
  Role,
  Farm,
  FarmAccessControl,
  InviteUserRequest,
  UpdateUserRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  ApiResponse,
  PaginatedResponse
} from '../types/settingsTypes';
import { apiClient } from '../../../services/api';
import { apiClientBff } from '../../../services/apiBff';
import { organizationRouter } from '../../organizations/services/organizationRouter';

// Settings service using real APIs only

// Mock farms removed - use real API data

class SettingsService {
  // User Management
  async getUsers(): Promise<ApiResponse<User[]>> {
    try {
      const result = await apiClientBff.get<{ data: User[] }>('/users');
      
      return {
        success: true,
        data: result.data || []
      };
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar usuários'
      };
    }
  }

  async getAvailableUsersForInvite(organizationId?: string): Promise<ApiResponse<User[]>> {
    try {
      const searchPayload = {
        query: `
          FOR profile IN UserProfile
            FILTER profile.profile_type != null
            RETURN profile
        `,
        bindVars: {}
      };

      const result = await apiClientBff.post<{ data?: any[]; result?: any[] }>('/profile/search', searchPayload);
      const rawProfiles = (result.data || result.result || []) as any[];
      const mappedUsers: User[] = rawProfiles.map(profile => ({
        _key: profile._key,
        username: profile.username,
        full_name: profile.contact_details?.full_name || profile.username,
        email: profile.contact_details?.email,
        phone: profile.contact_details?.phone,
        profile_type: profile.profile_type,
        professional_specializations: profile.professional_specializations || [],
        business_info: profile.business_info,
        created_at: profile.created_at
      } as User));
      
      return {
        success: true,
        data: mappedUsers,
        message: `Encontrados ${mappedUsers.length || 0} usuários disponíveis para convite`
      };
    } catch (error) {
      console.error('Erro ao buscar usuários disponíveis:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar usuários disponíveis'
      };
    }
  }

  async getUsersByFarm(farmId: string): Promise<ApiResponse<User[]>> {
    try {
      const searchPayload = {
        query: `
          FOR user IN Users
            FILTER LENGTH(user.farm_role_assignments[* FILTER CURRENT.farm_id == @farmId]) > 0
            RETURN user
        `,
        bindVars: {
          farmId: farmId
        }
      };

      const result = await apiClientBff.post<{ data: User[]; count?: number }>('/users/search', searchPayload);
      
      return {
        success: true,
        data: result.data || [],
        message: `Encontrados ${result.data?.length || 0} usuários para a fazenda ${farmId}`
      };
    } catch (error) {
      console.error('Erro ao buscar usuários da fazenda:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar usuários da fazenda'
      };
    }
  }

  async inviteUser(userRequest: InviteUserRequest): Promise<ApiResponse<User>> {
    try {
      const result = await apiClientBff.post<{ data: User }>('/users/invite', userRequest);
      
      return {
        success: true,
        data: result.data,
        message: 'Usuário convidado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao convidar usuário:', error);
      return {
        success: false,
        data: {} as User,
        error: 'Erro ao convidar usuário'
      };
    }
  }

  async updateUser(userId: string, updateRequest: UpdateUserRequest): Promise<ApiResponse<User>> {
    try {
      const result = await apiClientBff.put<{ data: User }>(`/users/${userId}`, updateRequest);
      
      return {
        success: true,
        data: result.data,
        message: 'Usuário atualizado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      return {
        success: false,
        data: {} as User,
        error: 'Erro ao atualizar usuário'
      };
    }
  }

  async deactivateUser(userId: string): Promise<ApiResponse<boolean>> {
    try {
      await apiClientBff.put(`/users/${userId}/deactivate`, { is_active: false });
      
      return {
        success: true,
        data: true,
        message: 'Usuário desativado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao desativar usuário:', error);
      return {
        success: false,
        data: false,
        error: 'Erro ao desativar usuário'
      };
    }
  }

  // Role Management
  async getRoles(): Promise<ApiResponse<Role[]>> {
    try {
      const result = await apiClientBff.post<{ data: Role[] }>('/roles/search', {
        bind_vars: {
          '@collection': 'Roles',
          limit: 200
        },
        query: 'FOR doc IN @@collection SORT doc.name ASC LIMIT @limit RETURN doc'
      });
      return {
        success: true,
        data: result.data || []
      };
    } catch (error) {
      console.error('Erro ao buscar cargos:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar cargos'
      };
    }
  }

  async createRole(roleRequest: CreateRoleRequest): Promise<ApiResponse<Role>> {
    try {
      const result = await apiClientBff.post<{ data: Role }>('/roles/insert', { data: roleRequest });
      
      return {
        success: true,
        data: result.data,
        message: 'Cargo criado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao criar cargo:', error);
      return {
        success: false,
        data: {} as Role,
        error: 'Erro ao criar cargo'
      };
    }
  }

  async updateRole(roleId: string, updateRequest: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    try {
      const result = await apiClientBff.put<{ data: Role }>(`/roles/${roleId}`, updateRequest);
      
      return {
        success: true,
        data: result.data,
        message: 'Cargo atualizado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao atualizar cargo:', error);
      return {
        success: false,
        data: {} as Role,
        error: 'Erro ao atualizar cargo'
      };
    }
  }

  async deleteRole(roleId: string): Promise<ApiResponse<boolean>> {
    try {
      await apiClientBff.delete(`/roles/${roleId}`);
      
      return {
        success: true,
        data: true,
        message: 'Cargo excluído com sucesso'
      };
    } catch (error) {
      console.error('Erro ao excluir cargo:', error);
      return {
        success: false,
        data: false,
        error: 'Erro ao excluir cargo'
      };
    }
  }

  // Farm Management
  async getFarms(profileId?: string): Promise<ApiResponse<Farm[]>> {
    try {
      const farmsData = await organizationRouter.getAllOrganizations(profileId);
      
      const farms = farmsData.map(farm => ({
        ...farm,
        _key: farm.id || (farm as any)._key || farm.name || 'unknown',
        farm_type: farm.farm_type === 'other' ? 'mixed' : farm.farm_type
      })) as unknown as Farm[];
      
      return {
        success: true,
        data: farms,
        message: `Encontradas ${farms.length} propriedades rurais${profileId ? ' do usuário' : ''}`
      };
    } catch (error) {
      console.error('Erro ao buscar propriedades rurais:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar propriedades rurais'
      };
    }
  }

  async addCollaboratorToFarm(
    farmId: string,
    collaborator: NonNullable<Farm['collaborators']>[number]
  ): Promise<ApiResponse<Farm>> {
    try {
      const currentFarm = await organizationRouter.getById(farmId, 'farm');
      if (!currentFarm) {
        return {
          success: false,
          data: {} as Farm,
          error: 'Fazenda não encontrada'
        };
      }

      const existingCollaborators = currentFarm.collaborators || [];
      const updatedCollaborators = existingCollaborators.filter(
        item => item.user_id !== collaborator.user_id
      );
      updatedCollaborators.push(collaborator);

      const updatedFarm = await organizationRouter.update(farmId, {
        collaborators: updatedCollaborators
      }, 'farm');

      if (!updatedFarm) {
        return {
          success: false,
          data: {} as Farm,
          error: 'Erro ao atualizar colaboradores'
        };
      }

      const normalizedFarm: Farm = {
        ...(updatedFarm as any),
        _key: (updatedFarm as any)._key || (updatedFarm as any).id || farmId,
      };

      return {
        success: true,
        data: normalizedFarm,
        message: 'Colaborador adicionado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao atualizar colaboradores:', error);
      return {
        success: false,
        data: {} as Farm,
        error: 'Erro ao atualizar colaboradores'
      };
    }
  }

  async searchFarmAccessControl(farmId: string): Promise<ApiResponse<FarmAccessControl[]>> {
    try {
      const payload = {
        bind_vars: {
          '@collection': 'FarmAccessControl',
          farmId,
        },
        query: 'FOR doc IN @@collection FILTER doc.farm_id == @farmId RETURN doc',
      };

      const result = await apiClientBff.post<{ data?: FarmAccessControl[] }>('/farm-access-control/search', payload);
      const data = result.data || [];
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Erro ao buscar FarmAccessControl:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar permissões da fazenda',
      };
    }
  }

  async createFarmAccessControl(doc: FarmAccessControl): Promise<ApiResponse<FarmAccessControl>> {
    try {
      const result = await apiClientBff.post<any>('/farm-access-control/insert', {
        data: doc,
      });
      const raw = result?.data ?? result?.result ?? result;
      const data = Array.isArray(raw) ? raw[0] : raw;
      return {
        success: true,
        data: data as FarmAccessControl,
      };
    } catch (error) {
      console.error('Erro ao criar FarmAccessControl:', error);
      return {
        success: false,
        data: {} as FarmAccessControl,
        error: 'Erro ao criar permissões da fazenda',
      };
    }
  }

  async updateFarmAccessControl(
    key: string,
    updates: Partial<FarmAccessControl>
  ): Promise<ApiResponse<FarmAccessControl>> {
    try {
      const payload = {
        bind_vars: {
          '@collection': 'FarmAccessControl',
          key,
          updates,
        },
        query: 'FOR doc IN @@collection FILTER doc._key == @key UPDATE doc WITH @updates IN @@collection RETURN NEW',
      };
      const result = await apiClientBff.post<any>('/farm-access-control/update', payload);
      const raw = result?.data ?? result?.result ?? result;
      const data = Array.isArray(raw) ? raw[0] : raw;
      return {
        success: true,
        data: (data as FarmAccessControl) || (updates as FarmAccessControl),
      };
    } catch (error) {
      console.error('Erro ao atualizar FarmAccessControl:', error);
      return {
        success: false,
        data: {} as FarmAccessControl,
        error: 'Erro ao atualizar permissões da fazenda',
      };
    }
  }

  async getUsersByIds(userIds: string[]): Promise<ApiResponse<User[]>> {
    try {
      const ids = userIds.filter(Boolean);
      if (ids.length === 0) {
        return {
          success: true,
          data: [],
        };
      }
      const payload = {
        bind_vars: {
          '@collection': 'UserProfile',
          ids,
          limit: ids.length,
        },
        query: `
          FOR doc IN @@collection
            FILTER doc._key IN @ids
            LIMIT @limit
            RETURN doc
        `,
      };
      const result = await apiClientBff.post<{ data?: any[]; result?: any[] }>(
        '/profile/search',
        payload
      );
      const rawProfiles = (result.data || result.result || []) as any[];
      const mappedUsers: User[] = rawProfiles.map((profile) => ({
        _key: profile._key,
        username: profile.username,
        full_name: profile.contact_details?.full_name || profile.username,
        email: profile.contact_details?.email,
        phone: profile.contact_details?.phone,
        profile_type: profile.profile_type,
        professional_specializations: profile.professional_specializations || [],
        business_info: profile.business_info,
        created_at: profile.created_at,
      })) as User[];
      return {
        success: true,
        data: mappedUsers,
      };
    } catch (error) {
      console.error('Erro ao buscar usuários por ids:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar usuários por ids',
      };
    }
  }

  async searchUsersByEmail(email: string): Promise<ApiResponse<User[]>> {
    try {
      const trimmed = email.trim().toLowerCase();
      const payload = {
        bind_vars: {
          '@collection': 'UserProfile',
          email: trimmed,
          limit: 20,
        },
        query: `
          FOR doc IN @@collection
            FILTER doc.contact_details != null
            FILTER doc.contact_details.email != null
            FILTER LIKE(LOWER(doc.contact_details.email), CONCAT('%', @email, '%'), true)
            LIMIT @limit
            RETURN doc
        `,
      };

      const result = await apiClientBff.post<{ data?: any[]; result?: any[] }>('/profile/search', payload);
      const rawProfiles = (result.data || result.result || []) as any[];
      const mappedUsers: User[] = rawProfiles.map((profile) => ({
        _key: profile._key,
        username: profile.username,
        full_name: profile.contact_details?.full_name || profile.username,
        email: profile.contact_details?.email,
        phone: profile.contact_details?.phone,
        profile_type: profile.profile_type,
        professional_specializations: profile.professional_specializations || [],
        business_info: profile.business_info,
        created_at: profile.created_at,
      })) as User[];
      return {
        success: true,
        data: mappedUsers,
      };
    } catch (error) {
      console.error('Erro ao buscar usuários por email:', error);
      return {
        success: false,
        data: [],
        error: 'Erro ao buscar usuários por email',
      };
    }
  }
}

export const settingsService = new SettingsService();
export default settingsService;
